import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Flex, Heading, Icon, Text } from '@opendoor/bricks/core';
import { IconName } from '@opendoor/bricks/core/Icon';
import { ParagraphTooltip } from '@opendoor/cinderblocks/exclusives/ParagraphTooltip';

import { trackAnswerChange } from 'src/components/fields/trackAnswerChange';
import { InitialSellingOptionsInterestQuestion } from 'src/lib/flow/FlowQuestion';
import { getAnswers } from 'src/store/sellerFlow/selectors';
import { changeAnswers, SavedAnswer } from 'src/store/sellerInput';

import { useCarousel } from '../helpers/useCarousel';

type InitialSellingOptionsInterestProps = {
  question: InitialSellingOptionsInterestQuestion;
  fieldsetLegend: string;
  values?: Record<string, boolean>;
};

const InitialSellingOptionsInterestPage: React.FC<InitialSellingOptionsInterestProps> = ({
  question,
  values,
}) => {
  const dispatch = useDispatch();
  const answers = useSelector(getAnswers);
  const { key } = question;

  const onClick = (value: string) => {
    const answer = values;
    const newSelections = answer ? { ...answer } : {};
    const isChecked = !newSelections[value];

    if (!isChecked && newSelections[value]) {
      // If we uncheck and was previous checked, we want to delete the key
      delete newSelections[value];
    } else {
      newSelections[value] = isChecked;
    }

    const newAnswers: Array<{ key: string; answer: SavedAnswer | null }> = [];
    if (Object.values(newSelections).length) {
      newAnswers.push({ key, answer: newSelections });
    } else {
      newAnswers.push({ key, answer: null });
    }
    dispatch(changeAnswers(newAnswers));
    trackAnswerChange(key, newSelections);
  };

  const value = answers[key];

  const preferenceCards = [
    <PreferenceCard
      description="Get a cash offer from Opendoor, and skip showings and making repairs"
      key="1"
      onChange={() => onClick('sell_direct')}
      title="Opendoor Cash"
      selectedInitial={value && value['sell_direct']}
      tab={<Tab text="Most Convenient" type="secondary" />}
      prosAndCons={[
        {
          type: 'pro',
          description: 'No making repairs',
        },
        {
          type: 'pro',
          description: 'No showings',
        },
        {
          type: 'con',
          description: 'Lowest offer',
        },
      ]}
      id="cash"
    />,
    <PreferenceCard
      description="Get the Opendoor cash offer and potentially higher offers from pre-qualified buyers"
      key="2"
      onChange={() => onClick('exclusives')}
      title="Opendoor Cash Plus"
      selectedInitial={value && value['exclusives']}
      tab={<Tab text="Convenience and Upside" type="primary" icon="star-filled" />}
      prosAndCons={[
        {
          type: 'pro',
          description: 'Cash offer locked in for 60 days',
        },
        {
          type: 'pro',
          description: (
            <>
              $5,000 Higher Offer
              <br />
              Guarantee{' '}
              <ParagraphTooltip analyticsName="exclusives-tooltip-fivek-offer-guarantee">
                Opendoor guarantees we will find a buyer to beat our Opendoor Cash offer by at least
                $5,000 in 30 days, or we will add $5,000 to our original offer price.
              </ParagraphTooltip>
            </>
          ),
        },
        {
          type: 'con',
          description: 'Limited showings from pre-qualified buyers',
        },
      ]}
      id="cash-plus"
    />,
    <PreferenceCard
      description="Get a local agent to help you list on the MLS"
      key="3"
      onChange={() => onClick('traditional_listing')}
      title="Opendoor Agent"
      selectedInitial={value && value['traditional_listing']}
      tab={<Tab text="Most Upside" type="secondary" />}
      id="traditional-listing"
      prosAndCons={[
        {
          type: 'con',
          description: 'No cash offer locked in for 60 days',
        },
        {
          type: 'con',
          description: 'No Higher Offer Guarantee',
        },
      ]}
    />,
  ];

  const carousel = useCarousel(preferenceCards);

  return (
    <Box w="100vw">
      <Box maxW={1025} mx="auto" px={6}>
        <Box mb={[0, null, 10]} mt="-10">
          <Heading as="h1" pb={[3, null, 6]} size="170" my="0">
            We offer multiple ways to sell. Which work best for you?
          </Heading>
          <Text as="p" size="120">
            Pick one or all - there's no commitment and you can cancel anytime, without penalty.
          </Text>
        </Box>
        <Box>
          <Box>{carousel.jsx}</Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InitialSellingOptionsInterestPage;

function Tab({
  text,
  icon,
  type,
}: {
  text: string;
  icon?: IconName;
  type: 'primary' | 'secondary';
}) {
  return (
    <Flex
      width={['264px', null, '90%', '261px']}
      mx="auto"
      justifyContent="center"
      alignItems="center"
      backgroundColor={type === 'primary' ? 'warmgrey950' : 'warmgrey200'}
      borderTopRadius="roundedSquare"
      height={Tab.height}
      py="1"
    >
      {icon && <Icon name={icon} size={16} mb="3px" mr="5px" color="neutrals0" />}
      <Text fontSize="14px" color={type === 'primary' ? 'neutrals0' : 'warmgrey800'}>
        {text}
      </Text>
    </Flex>
  );
}
Tab.height = 28;

type ProsAndCons = {
  type: 'pro' | 'con';
  description: React.ReactNode;
}[];

function PreferenceCard({
  description,
  onChange,
  selectedInitial,
  title,
  tab,
  prosAndCons,
  id,
}: {
  description: React.ReactNode;
  onChange: (selected: boolean) => void;
  selectedInitial: boolean;
  title: string;
  tab: React.ReactNode;
  prosAndCons?: ProsAndCons;
  id: string;
}) {
  const [selected, setSelected] = useState(selectedInitial);
  return (
    <Flex
      aria-checked={selected}
      aria-label={`Select ${title}`}
      minHeight="286px"
      cursor="pointer"
      flexDirection="column"
      mt={[6, null, 0]}
      onClick={() => {
        setSelected(!selected);
        onChange?.(!selected);
      }}
      onKeyDown={(e) => {
        switch (e.code) {
          case 'Enter':
            (e.target as HTMLDivElement).closest('form')?.requestSubmit();
            break;
          case 'Space':
            e.preventDefault(); // prevent auto-scrolling
            setSelected(!selected);
            onChange?.(!selected);
            break;
        }
      }}
      role="checkbox"
      tabIndex={0}
      title={`Select ${title}`}
      _hover={{
        borderColor: selected ? 'neutrals100' : 'neutrals80',
      }}
      position="relative"
      id={id}
    >
      {tab}
      <Box
        backgroundColor="warmgrey100"
        borderColor={selected ? 'neutrals100' : 'warmgrey100'}
        borderRadius="semiRounded"
        borderWidth={3}
        borderStyle="solid"
        p={24}
        width="100%"
        height={`calc(100% - ${Tab.height}px)`}
      >
        <Box
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          pb={['unset', null, 3]}
        >
          <Flex justifyContent="space-between" width="100%" alignItems="center">
            <Text size="150" fontWeight="medium" letterSpacing="-3">
              {title}
            </Text>
            <Box alignSelf="flex-start">
              {selected ? (
                <img
                  alt="checkedbox-checked"
                  src="https://imgdrop.imgix.net/2023-07-1689870968141-05016.svg"
                />
              ) : (
                <img
                  alt="checkedbox-unchecked"
                  src="https://imgdrop.imgix.net/2023-07-1689870968147-93667.svg"
                />
              )}
            </Box>
          </Flex>
        </Box>

        <Text as="p" pb={8} size="120" marginTop="4" minHeight={['unset', null, '110px', '85px']}>
          {description}
        </Text>
        {prosAndCons && (
          <Flex flexDir="column" gap={4}>
            {prosAndCons.map((item, i) => (
              <Flex key={i} justifyContent="flex-start">
                <Icon
                  name={item.type === 'pro' ? 'checkmark' : 'minus'}
                  size={16}
                  color="neutrals0"
                  backgroundColor={item.type === 'pro' ? 'success500' : 'warmgrey700'}
                  borderRadius="rounded"
                  padding="2"
                  marginRight="3"
                />
                <Flex>
                  <Text size="110">{item.description}</Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        )}
      </Box>
    </Flex>
  );
}
